import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaFileContract,
  FaGavel,
  FaBuilding,
  FaPlusCircle,
  FaFolderOpen,
} from "react-icons/fa";
import api from "../utils/api";

// ✅ الفئات القانونية الجاهزة
const categories = [
  {
    title: "📜 نماذج الشهر العقاري",
    color: "from-yellow-400 via-orange-400 to-red-500",
    icon: <FaFileContract className="text-4xl mb-3 text-yellow-300" />,
    docs: [
      { title: "عقد بيع نهائي", type: "finalSale" },
      { title: "عقد بيع ابتدائي", type: "preliminarySale" },
      { title: "عقد إيجار موثق", type: "officialRent" },
      { title: "عقد قسمة", type: "division" },
      { title: "توكيل رسمي عام", type: "generalPower" },
      { title: "توكيل رسمي خاص", type: "specialPower" },
      { title: "إقرار تبرع", type: "donation" },
      { title: "إقرار تنازل", type: "waiver" },
      { title: "عقد رهن", type: "mortgage" },
      { title: "محضر تصالح", type: "reconciliation" },
    ],
  },
  {
    title: "⚖️ نماذج المحاكم والدعاوى",
    color: "from-blue-400 via-indigo-400 to-purple-500",
    icon: <FaGavel className="text-4xl mb-3 text-indigo-300" />,
    docs: [
      { title: "صحيفة دعوى مدنية", type: "civilCase" },
      { title: "إنذار قضائي", type: "warning" },
      { title: "صحيفة استئناف", type: "appeal" },
      { title: "صحيفة طعن بالنقض", type: "cassation" },
      { title: "صحيفة جنحة مباشرة", type: "criminalComplaint" },
      { title: "مذكرة دفاع", type: "defenseMemo" },
      { title: "صحيفة تنفيذ حكم", type: "executionNotice" },
    ],
  },
  {
    title: "💼 عقود الشركات",
    color: "from-emerald-400 via-green-400 to-lime-400",
    icon: <FaBuilding className="text-4xl mb-3 text-emerald-300" />,
    docs: [
      { title: "عقد تأسيس شركة", type: "companyEstablishment" },
      { title: "عقد شراكة", type: "partnership" },
      { title: "عقد وكالة بالعمولة", type: "commissionAgency" },
      { title: "عقد خدمات استشارية", type: "consulting" },
      { title: "اتفاقية عدم إفشاء (NDA)", type: "nda" },
      { title: "عقد مقاولة", type: "contracting" },
    ],
  },
];

function LegalDocs() {
  const navigate = useNavigate();
  const [customTemplates, setCustomTemplates] = useState([]);

  // 🔄 تحميل النماذج التي أنشأها المستخدم
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get("/templates");
        setCustomTemplates(res.data);
      } catch (err) {
        console.error("❌ فشل في تحميل النماذج:", err.message);
      }
    };
    fetchTemplates();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 text-white p-8">
      {/* 🏛️ العنوان الرئيسي */}
      <motion.h1
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-3xl md:text-4xl font-extrabold text-center mb-12 text-yellow-400 drop-shadow-[0_0_10px_rgba(255,204,0,0.6)]"
      >
        ⚖️ نظام إدارة وصياغة العقود والمستندات القانونية
      </motion.h1>

      {/* ➕ إنشاء نموذج جديد */}
      <motion.div
        onClick={() => navigate("/templates-manager")}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="cursor-pointer bg-gradient-to-br from-yellow-400 to-orange-500 text-black p-6 rounded-3xl shadow-2xl mx-auto max-w-xs text-center mb-12 transition-all hover:shadow-yellow-500/50"
      >
        <FaPlusCircle className="text-6xl mx-auto mb-3" />
        <h3 className="text-xl font-bold">إنشاء نموذج قانوني جديد</h3>
        <p className="text-sm mt-1 text-gray-800">
          صمّم نموذجك الخاص واحتفظ به ضمن مكتبتك القانونية
        </p>
      </motion.div>

      {/* 🗂️ عرض الفئات القانونية */}
      <div className="space-y-14">
        {categories.map((cat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.2 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              {cat.icon}
              <h2 className="text-2xl font-semibold text-yellow-300 drop-shadow-sm">
                {cat.title}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {cat.docs.map((doc, j) => (
                <motion.div
                  key={j}
                  onClick={() => navigate(`/legal-docs/${doc.type}`)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className={`cursor-pointer bg-gradient-to-br ${cat.color} p-6 rounded-2xl shadow-lg hover:shadow-yellow-300/40 transition-all`}
                >
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-1">
                    {doc.title}
                  </h3>
                  <p className="text-gray-800 text-sm text-center">
                    اضغط للبدء في إعداد هذا النموذج
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}

        {/* 📁 النماذج التي أنشأها المحامي */}
        {customTemplates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="flex items-center gap-3 mb-6">
              <FaFolderOpen className="text-yellow-400 text-3xl" />
              <h2 className="text-2xl font-semibold text-yellow-300">
                📂 النماذج التي أنشأتها
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {customTemplates.map((t, i) => (
                <motion.div
                  key={i}
                  onClick={() => navigate(`/generate/${t._id}`)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  className="cursor-pointer bg-gradient-to-br from-gray-800 to-gray-700 p-6 rounded-2xl shadow-lg hover:shadow-yellow-400/40 transition-all"
                >
                  <h3 className="text-lg font-bold text-yellow-400 text-center mb-1">
                    {t.name}
                  </h3>
                  <p className="text-sm text-gray-300 text-center">
                    نموذج محفوظ في مكتبتك القانونية
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* ⚖️ تذييل الصفحة */}
      <footer className="mt-20 text-center text-gray-500 text-sm border-t border-gray-800 pt-6">
        <p>الافوكاتو المحامي الذكي</p>
      </footer>
    </div>
  );
}

export default LegalDocs;
